<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-5xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BookOpen class="w-5 h-5 text-brand-600" />
            {{ form.id ? 'Editar Asiento Contable' : 'Nuevo Asiento Contable' }}
          </h2>
          <p class="text-xs text-slate-500">Registra un nuevo comprobante de diario (Partida).</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form id="journalForm" @submit.prevent="handleSubmit" class="space-y-6" novalidate>

          <div class="bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800">
            <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-4 uppercase tracking-tight border-b border-slate-200 dark:border-slate-700 pb-2">
              Datos del Comprobante
            </h3>

            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Fecha contable</label>
                <input v-model="form.date" type="date" required :class="inputClasses" />
              </div>
              <div>
                <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">No. Referencia (Opcional)</label>
                <input v-model="form.reference" type="text" placeholder="Ej. CHQ-1029, FAC-992" :class="inputClasses" />
              </div>
              <div class="md:col-span-2">
                <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Concepto General</label>
                <input v-model="form.concept" type="text" required placeholder="Descripción general del asiento..." :class="inputClasses" />
              </div>
            </div>
          </div>

          <div class="bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800">
            <div class="flex justify-between items-center mb-4 border-b border-slate-200 dark:border-slate-700 pb-2">
              <h3 class="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-tight">
                Detalle de Movimientos
              </h3>
              <button type="button" @click="addLine" class="text-[10px] bg-brand-100 text-brand-700 hover:bg-brand-200 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1 transition-colors">
                <Plus class="w-3 h-3" /> Agregar Línea
              </button>
            </div>

            <div class="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
              <table class="w-full text-xs text-left border-collapse bg-white dark:bg-slate-900">
                <thead class="bg-slate-100 dark:bg-slate-800 text-slate-500 uppercase">
                  <tr>
                    <th class="px-3 py-3 w-1/3">Cuenta Contable</th>
                    <th class="px-3 py-3 w-1/3">Descripción (Opcional)</th>
                    <th class="px-3 py-3 w-1/6 text-right">Debe</th>
                    <th class="px-3 py-3 w-1/6 text-right">Haber</th>
                    <th class="px-3 py-3 w-12 text-center"></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                  <tr v-for="(line, index) in form.lines" :key="index" class="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                    <td class="px-2 py-2">
                      <select v-model="line.account_id" required class="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-slate-700 dark:text-slate-200">
                        <option value="" disabled>Seleccione una cuenta...</option>
                        <option v-for="acc in selectableAccounts" :key="acc.id" :value="acc.id">
                          {{ acc.code }} - {{ acc.name }}
                        </option>
                      </select>
                    </td>
                    <td class="px-2 py-2">
                      <input v-model="line.description" type="text" placeholder="Glosa de la línea" class="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-slate-700 dark:text-slate-200" />
                    </td>
                    <td class="px-2 py-2">
                      <input v-model.number="line.debit" @input="line.credit = 0" type="number" step="0.01" min="0" class="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-right focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-slate-900 font-mono" />
                    </td>
                    <td class="px-2 py-2">
                      <input v-model.number="line.credit" @input="line.debit = 0" type="number" step="0.01" min="0" class="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-right focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-slate-900 font-mono" />
                    </td>
                    <td class="px-2 py-2 text-center">
                      <button type="button" @click="removeLine(index)" :disabled="form.lines.length <= 2" class="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        <Trash2 class="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                  <tr>
                    <td colspan="2" class="px-4 py-3 text-right font-bold text-slate-600 dark:text-slate-300 uppercase text-xs">
                      Totales
                    </td>
                    <td class="px-3 py-3 text-right font-mono font-bold text-sm" :class="isBalanced ? 'text-slate-900 dark:text-white' : 'text-red-500'">
                      ${{ totalDebit.toFixed(2) }}
                    </td>
                    <td class="px-3 py-3 text-right font-mono font-bold text-sm" :class="isBalanced ? 'text-slate-900 dark:text-white' : 'text-red-500'">
                      ${{ totalCredit.toFixed(2) }}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div v-if="!isBalanced && (totalDebit > 0 || totalCredit > 0)" class="mt-4 flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl border border-red-100 text-xs font-bold">
              <AlertCircle class="w-4 h-4" />
              El comprobante está descuadrado. Diferencia: ${{ Math.abs(totalDebit - totalCredit).toFixed(2) }}
            </div>

          </div>
        </form>
      </div>

      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 z-20">
        <button @click="$emit('close')" type="button" class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors">
          Cancelar
        </button>
        <button
          type="submit"
          form="journalForm"
          :disabled="isSaving || !isBalanced || totalDebit === 0"
          class="px-6 py-2.5 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
          Guardar Asiento
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { X, Save, Loader2, BookOpen, Plus, Trash2, AlertCircle } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({ show: Boolean, entryToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-slate-700 dark:text-slate-200";

const isSaving = ref(false);
const allAccounts = ref([]);

// Formulario reactivo
const form = reactive({
  id: null,
  date: new Date().toISOString().split('T')[0],
  reference: '',
  concept: '',
  lines: [
    { account_id: '', description: '', debit: 0, credit: 0 },
    { account_id: '', description: '', debit: 0, credit: 0 }
  ]
});

// Cargar catálogo de cuentas al montar el componente
onMounted(async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user?.company) return;
  try {
    const response = await apiClient.get(`/acc_accounts?company_id=${user.company}`);
    allAccounts.value = response.data.data || response.data;
  } catch (error) {
    showError('Error al cargar el catálogo de cuentas.');
  }
});

// Filtrar SOLO las cuentas "Movimiento" / Seleccionables para el dropdown
const selectableAccounts = computed(() => {
  return allAccounts.value
    .filter(acc => acc.is_selectable === 1 || acc.is_selectable === true)
    .sort((a, b) => a.code.localeCompare(b.code));
});

// Cálculos de Partida Doble
const totalDebit = computed(() => {
  return form.lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
});

const totalCredit = computed(() => {
  return form.lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
});

const isBalanced = computed(() => {
  // Tienen que ser iguales, redondeados a 2 decimales, y mayores a 0
  return totalDebit.value > 0 && Math.abs(totalDebit.value - totalCredit.value) < 0.01;
});

// Métodos para las líneas
const addLine = () => {
  form.lines.push({ account_id: '', description: '', debit: 0, credit: 0 });
};

const removeLine = (index) => {
  if (form.lines.length > 2) {
    form.lines.splice(index, 1);
  }
};

// Resetear o Cargar datos cuando se abre/cierra el modal
watch(() => props.show, (val) => {
  if (val) {
    if (props.entryToEdit && Object.keys(props.entryToEdit).length > 0) {
      // Lógica por si quieres pasarle un asiento existente para editar
      Object.assign(form, props.entryToEdit);
    } else {
      // Limpiar para un asiento nuevo
      Object.assign(form, {
        id: null,
        date: new Date().toISOString().split('T')[0],
        reference: '',
        concept: '',
        lines: [
          { account_id: '', description: '', debit: 0, credit: 0 },
          { account_id: '', description: '', debit: 0, credit: 0 }
        ]
      });
    }
  }
});

// Enviar datos
const handleSubmit = async () => {
  if (!isBalanced.value) {
    return showError('El asiento no está cuadrado. Revisa los totales.');
  }

  // Validar que todas las líneas tengan cuenta seleccionada
  const hasEmptyAccounts = form.lines.some(l => !l.account_id);
  if (hasEmptyAccounts) {
    return showError('Todas las líneas deben tener una cuenta contable asignada.');
  }

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const payload = {
      ...form,
      company_id: user.company,
      total_amount: totalDebit.value // Se asume que el monto total del comprobante es el Debe (que es igual al Haber)
    };

    if (form.id) {
      await apiClient.put(`/acc_journal_entries/${form.id}`, payload);
      showSuccess('Asiento contable actualizado');
    } else {
      await apiClient.post('/acc_journal_entries', payload);
      showSuccess('Asiento contable registrado exitosamente');
    }

    emit('saved');
    emit('close');
  } catch (error) {
    showError(error.response?.data?.message || 'Error al guardar el asiento contable.');
  } finally {
    isSaving.value = false;
  }
};
</script>